import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  CardActions,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  ViewButton,
} from '@/components/ui';
import { StudentIdentityActions } from '@/features/admin/StudentIdentityActions';
import { graduationYearParam } from '@/features/admin/filters';
import { VERIFICATION_TONES, humanizeStatus } from '@/features/admin/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';

/**
 * Verifying a student is not the same shape of job as verifying a company.
 *
 * A company submits and waits, so its screen is a queue that empties. A
 * student submits nothing for this identity check (diplomas and CVs are
 * checked separately, on the document page), so this is the
 * student register narrowed to the decision an admin has or has not made, and
 * the decision is taken on the evidence the platform already holds: a verified
 * university address, a filled-in profile, real applications.
 *
 * Hence "Undecided" rather than "Pending", and no count on the rail: everybody
 * starts undecided, and a badge reading 190 would be alarm without meaning.
 */
const TABS = [
  { key: 'UNVERIFIED', label: 'Undecided' },
  { key: 'VERIFIED', label: 'Verified' },
  { key: 'REJECTED', label: 'Refused' },
];

const PAGE_SIZE = 20;

export function AdminStudentVerificationsPage() {
  const [status, setStatus] = useState('UNVERIFIED');
  const [search, setSearch] = useState('');
  const [proof, setProof] = useState('');
  const [graduationYear, setGraduationYear] = useState('');
  const [page, setPage] = useState(1);

  const year = graduationYearParam(graduationYear);
  const params = {
    page,
    page_size: PAGE_SIZE,
    statuses: status,
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(proof ? { university_email_verified: proof === 'yes' } : {}),
    ...year,
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-student-verifications', params],
    queryFn: () => adminApi.listStudentVerifications(params),
    // Without this every keystroke unmounts the page for the loading state,
    // taking the search box and the focus in it.
    placeholderData: keepPreviousData,
  });

  /** Every filter change is a new result set, so page 1 is the only sane page. */
  function change(setter) {
    return (event) => {
      setter(event.target.value);
      setPage(1);
    };
  }

  const filtered = Boolean(search.trim() || proof || year.graduation_year);

  const activeCount = [search !== '', proof !== '', graduationYear !== ''].filter(Boolean).length;

  function resetFilters() {
    setSearch('');
    setProof('');
    setGraduationYear('');
    setPage(1);
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div>
      <PageHeader
        title="Verify students"
        description="Confirm a student is who they say they are, on the evidence the platform holds."
      />

      {status === 'UNVERIFIED' && !filtered && (
        <Alert tone="info" title="There is nothing to wait for here" className="mb-6">
          Students do not submit anything for review, so this is not a queue that empties — it is
          everybody nobody has decided about yet, strongest evidence first. A verified university
          address is the best proof the platform has.
        </Alert>
      )}

      <ListCard
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            tabs={TABS}
            value={status}
            onChange={(key) => {
              setStatus(key);
              setPage(1);
            }}
            label="Student verification status"
          />
        }
        toolbarEnd={
          <>
            {/* The one filter that turns this list into a working queue: the
                students who have proved a university address are the ones a
                decision can actually be taken on. */}
            <Select
              label="University email"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={proof}
              onChange={change(setProof)}
            >
              <option value="">Any university email</option>
              <option value="yes">Proved</option>
              <option value="no">Not proved</option>
            </Select>
            <Input
              label="Graduating"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-32"
              type="number"
              placeholder="Graduating"
              value={graduationYear}
              onChange={change(setGraduationYear)}
            />
            <SearchInput
              label="Search students"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Name, account email or university email"
              value={search}
              onChange={change(setSearch)}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading students…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                title="Nothing matches these filters."
                description="No students in this state match these filters."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState title="Nobody here" description="No students in this state right now." />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => {
            const studying = [row.field_of_study, row.graduation_year].filter(Boolean).join(' · ');
            return (
              <BoxRow
                key={row.id}
                accent={VERIFICATION_TONES[row.identity_verification_status] ?? 'neutral'}
                leading={<BoxAvatar name={row.full_name} round />}
                title={row.full_name}
                to={paths.admin.studentDetail(row.id)}
                badges={
                  <Badge
                    dot
                    tone={VERIFICATION_TONES[row.identity_verification_status] ?? 'neutral'}
                  >
                    {row.identity_verification_status === 'UNVERIFIED'
                      ? 'Undecided'
                      : humanizeStatus(row.identity_verification_status)}
                  </Badge>
                }
                subtitle={row.email}
                facts={[
                  { icon: 'university', text: row.university ?? 'University not given' },
                  // The one thing here the student proved rather than typed,
                  // which is why it carries the colour.
                  row.university_email_verified
                    ? { icon: 'shieldCheck', text: 'University email verified', tone: 'success' }
                    : { icon: 'alert', text: 'University email unverified' },
                  {
                    icon: 'graduationCap',
                    text: [row.degree ?? 'No degree given', studying].filter(Boolean).join(' · '),
                  },
                  { icon: 'userCheck', text: `${row.profile_completion}% profile` },
                  {
                    icon: 'application',
                    text: `${row.application_count} ${
                      row.application_count === 1 ? 'application' : 'applications'
                    }`,
                  },
                ]}
                // The identity decision is the main action here, so View
                // steps back to secondary and goes first.
                actions={
                  <CardActions>
                    <ViewButton
                      to={paths.admin.studentDetail(row.id)}
                      title={`Open ${row.full_name}`}
                      variant="secondary"
                    />
                    <StudentIdentityActions student={row} compact className="contents" />
                  </CardActions>
                }
              />
            );
          })}
        </BoxList>
      </ListCard>
    </div>
  );
}
